import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIconButton from '../../components/DeleteIconButton';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import { Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, Link, MenuItem, Select, Tab, Tabs, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '../../components/TimePicker';
import { format, parseISO } from 'date-fns';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { QRCodeCanvas } from 'qrcode.react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Prompt } from 'react-router';

import { CREATE, DataAPIContext, GET_LIST, UPDATE } from '../../api';
import AccessDeniedView from '../../components/AccessDeniedView';
import FileActionRow from '../../components/FileActionRow';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import FormActionButtons from '../../components/FormActionButtons';
import PageMeta from '../../components/PageMeta';
import { RecordDetails, RecordDetailsContext } from '../../components/RecordDetails';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';
import RevisionHistoryDialog from '../../components/RevisionHistoryDialog';
import ChangeBookingOwnerDialog from './ChangeBookingOwnerDialog';
import TabPanel, { a11yProps } from '../../components/TabPanel';
import { ContextStore } from '../../store/contextStore';
import { toTitleCase, validateEmail } from '../../utils';
import { copyTextToClipboard } from '../../utils';
import { convertToAmPm, isUSLocale } from '../../utils/date';
import { tourUsesOperatorDefaults } from '../../utils/tagCategories';
import fileToSha1String from '../../utils/file_to_sha1';
import { extractStartAndEndDate } from '../utils';
import CheckoutPicker from './CheckoutPicker';
import { checkoutForNewTripDate } from './checkoutTime';
import BookingTickets from './BookingTickets';
import { applyTicketChanges, hasTicketChanges } from './ticketChanges';
import PrecheckinAnswers from './PrecheckinAnswers';
import { blank_trip_user, compare, bookingStatesMenuItems, ERROR_MESSAGES, SOURCES, surveyStatesMenuItems } from './utils';

// Group_Tour_User.END_TS_TYPE_* -- whether a booking derives its end from the tour's
// itinerary length (ITINERARY, the default) or carries its own end datetime (BOOKING,
// e.g. a hotel stay). The server owns this discriminator: the edit API derives it from
// whether end_ts is present, so the form sends end_ts and treats end_ts_type as local
// render state only.
// The panel sits at zIndex 10000 (classes.root), above the tooltip layer.
const ABOVE_PANEL_TOOLTIP = { PopperProps: { style: { zIndex: 10001 } } };

const END_TS_TYPE_ITINERARY = 0;
const BOOKING_OWN_END = 1;

const useStyles = makeStyles((theme) => {
    return {
        root: {
            border: '1px solid #d8d8d8',
            borderRadius: '5px',
            backgroundColor: theme.palette.background.paper,
            zIndex: 10000,
            overflowY: 'auto',
            [theme.breakpoints.down('sm')]: {
                position: 'fixed',
                top: '65px',
                right: '0.25rem',
                left: '0.25rem',
                bottom: '1rem',
            },
        },
        heading: {
            marginBottom: '1rem',
            [theme.breakpoints.down('sm')]: {
                marginBottom: '0rem',
            },
        },
        tagListContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
        },
        tagCapsule: {
            padding: '0 0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '150px',
            width: '100%',
            border: 'solid 1px gray',
            borderRadius: '15px',
            backgroundColor: 'lightgray',
        },
        tagCapsuleDeleteButton: {},
        nearlyHidden: {
            color: grey[200],
        },
        formWrapper: {
            height: `calc(${theme.cp.contentHeight} - 22.5rem) !important`,
            overflowY: 'auto',
            overflowX: 'hidden',
            [theme.breakpoints.down('sm')]: {
                height: `calc(${theme.cp.contentHeight} - 12rem) !important`,
            },
        },
        changeHistoryWrapper: {
            [theme.breakpoints.down('sm')]: {
                height: `calc(${theme.cp.contentHeight} - 13rem) !important`,
            },
        },
        buttonsPanel: {
            backgroundColor: theme.palette.background.paper,
            paddingTop: '1rem'
        }
    };
});

let GuestRoleUniqId = Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));

const validateUser = (tour_user) => {
    const errors = {};
    let canSave = true;
    if (!tour_user || !tour_user.user || !tour_user.user.id) {
        if (!tour_user || !tour_user.user || !tour_user.user.email || !validateEmail(tour_user.user.email)) {
            if (tour_user && tour_user.user._changed) {
                errors.email = { error: true, helperText: 'Email is required' };
            }
            canSave = false;
        }
    }
    if (tour_user.user.email) {
        if (!validateEmail(tour_user.user.email)) {
            errors.email = { error: true, helperText: ERROR_MESSAGES['validEmailAddress'] };
            canSave = false
        }
    }
    if (tour_user.tags.filter((tag) => !tag.id).length > 0) {
        errors.tags = { error: true, helperText: 'Required tag is empty' };
        canSave = false;
    }

    if (tour_user && tour_user.serverErrors) {
        tour_user.serverErrors.forEach((err) => {
            if (!tour_user['_' + err.field + 'Changed']) {
                errors[err.field] = { error: true, helperText: err.message };
            }
        });
    }
    return { canSave, errors };
};


const formatUserData = (user_data) => {
    let new_user_data = { ...user_data };
    for (let i = 0; i < user_data.tags.length; i++) {
        if (!('idx' in new_user_data.tags[i])) {
            new_user_data.tags[i] = { ...user_data.tags[i], idx: i };
        }
    }

    for (let i = 0; i < user_data.attachments.length; i++) {
        // check if the structure is already correct to prevent nesting
        if (!('file' in user_data.attachments[i]) || !('idx' in user_data.attachments[i])) {
            new_user_data.attachments[i] = { file: user_data.attachments[i], idx: i };
        }
    }
    return new_user_data;
};

const normalizeTripUserState = (data) => {
    const formatted = formatUserData(data);
    const user = { ...(formatted.user || {}) };
    delete user._changed;
    const { _changed, ...rest } = formatted;
    return {
        ...rest,
        user,
        role: formatted.role
            ? formatted.role
            : {
                  id: GuestRoleUniqId,
                  name: 'Guest',
              },
    };
};

const filteredMaxedTags = (tag_categories, tags) => {
    let filtered = [...tag_categories];

    filtered = filtered.filter((tc) => tc.category.code !== 'departure');

    for (let index = 0; index < tag_categories.length; index++) {
        if (tag_categories[index]?.max_tags === 1) {
            if (tags.filter((tag) => tag?.category?.id === tag_categories[index]?.category?.id).length > 0) {
                filtered = filtered.filter((atc) => atc?.id !== tag_categories[index]?.id);
            }
        }
    }

    return filtered;
};

const mapped_user_roles = (tripUser, roleMap) => {
    let allowedRoles = [];

    let guestRole = {
        id: GuestRoleUniqId,
        name: 'Guest',
    };

    // add the current role to allowed roles (#CP-1742)
    if (tripUser.role && tripUser.role.id !== GuestRoleUniqId) {
        allowedRoles = [roleMap[tripUser.role.id]];
    }

    // add staff allowed roles
    if (tripUser.user.is_tour_operator_staff || tripUser.user.roles?.length > 0) {
        tripUser?.user.roles.forEach((r) => {
            if (tripUser.role.id !== r) {
                return allowedRoles.push(roleMap[r]);
            }
        });
    } else {
        // guest user with previous role
        allowedRoles = [...allowedRoles, guestRole];
    }

    return allowedRoles;
};

const TripUserPreviewPanel = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'tour_user';
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false); // Flag to prevent Prompt during save
    const [missingRequiredTagsError, setMissingRequiredTagsError] = React.useState(false);
    const [sameDateExistsError, setSameDateExistsError] = React.useState(false);
    const saveRef = React.useRef(null);
    const { roleMap, hasPerm, tourOperator } = React.useContext(ContextStore);
    const [changeOwnerOpen, setChangeOwnerOpen] = React.useState(false);

    // Merge trip-level categories with operator default categories (fresh from ContextStore),
    // but only when the trip actually uses operator defaults.
    const mergedTagCategories = React.useMemo(() => {
        const tripCategories = props.trip.tag_categories || [];
        if (!tourUsesOperatorDefaults(props.trip, tourOperator)) {
            return tripCategories;
        }
        const operatorDefaults = tourOperator?.default_tag_categories || [];

        // Build a set of category IDs that exist at trip level
        const tripCategoryIds = new Set(
            tripCategories.filter(c => c.category?.id).map(c => c.category.id.toString())
        );

        // Add operator defaults that aren't already in trip categories
        const missingDefaults = operatorDefaults.filter(
            cat => !tripCategoryIds.has(cat.category?.id?.toString())
        );

        return [...tripCategories, ...missingDefaults];
    }, [props.trip, tourOperator]);
    const [currentTab, setCurrentTab] = React.useState(0);
    const theme = useTheme();
    const tripTimeZone = props.trip.start_timezone || 'UTC';
    const baselineRef = React.useRef({ email: '', phone: '' });
    const [tripUser, setTripUser] = React.useState(() => {
        const state = normalizeTripUserState(props.data);
        baselineRef.current = {
            email: state.user?.email ?? '',
            phone: state.user?.phone ?? '',
        };
        return state;
    });

    React.useEffect(() => {
        const state = normalizeTripUserState(props.data);
        baselineRef.current = {
            email: state.user?.email ?? '',
            phone: state.user?.phone ?? '',
        };
        setTripUser(state);
    }, [props.data.id]);

    const allowedTagCategories = filteredMaxedTags(mergedTagCategories, tripUser.tags);
    const allowed_roles = mapped_user_roles(tripUser, roleMap);
    const { errors } = validateUser(tripUser);
    const canBeSaved = !tripUser.id || tripUser._changed;
    const nonBespokeStartDate = extractStartAndEndDate(props.trip.schedule)?.startDate
    const nonBespokeEndDate = extractStartAndEndDate(props.trip.schedule)?.endDate
    const hasWritePerm = hasPerm('tour_participant', 'write')

    // add required categories tags in tags array
    React.useEffect(() => {
        // Only add placeholder tags for new users, not existing ones loaded from API
        if (tripUser.id) {
            return;
        }

        let updateTagsArray = [...tripUser.tags];
        let requiredCategories = mergedTagCategories.filter((cat) => cat.min_tags > 0);

        requiredCategories = requiredCategories.filter((tc) => tc.category.code !== 'departure');

        for (let i = 0; i < requiredCategories.length; i++) {
            let newTag = { idx: updateTagsArray.length + 1, category: requiredCategories[i].category, _changed: false };
            // in cases of registering a trip user not from the dashboard
            if (tripUser.tags.filter((tag) => tag.category?.id === newTag.category?.id).length === 0) {
                updateTagsArray.push(newTag);
            }
        }
        setTripUser((prevState) => ({ ...prevState, tags: updateTagsArray }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };
    const onExistingUserSelect = (ev, nv) => {
        if (nv) {
            setTripUser((prevState) => ({ ...prevState, user: { ...nv }, _changed: true }));
        } else {
            setTripUser((prevState) => ({ ...prevState, user: { ...blank_trip_user.user }, _changed: true }));
        }
    };
    const onUserEmailChange = (nv) => {
        setTripUser((prev) => {
            const nextEmail = nv ?? '';
            const emailDirty = nextEmail !== (baselineRef.current?.email ?? '');
            return {
                ...prev,
                user: { ...prev.user, email: nextEmail },
                _changed: prev._changed || emailDirty,
            };
        });
    };

    const handleOnPhoneChange = (number, numberProps) => {
        setTripUser((prev) => {
            const phoneDirty = number !== (baselineRef.current?.phone ?? '');
            return {
                ...prev,
                user: { ...prev.user, phone: number },
                _phoneChanged: phoneDirty,
                _changed: prev._changed || phoneDirty,
            };
        });
    };

    const onLanguageChange = (ev, lang, reason) => {
        console.log('lang change', lang);
        setTripUser((prevState) => ({
            ...prevState,
            user: {
                ...prevState.user,
                lang: lang?.id || prevState.user.lang,
            },
            _changed: true,
        }));
    };

    const onChange = (ev) => {
        if (ev.target.name === 'booking_notes' || ev.target.name === 'checkin_notes') {
            const upd = { ...tripUser, [ev.target.name]: ev.target.value, _changed: true };
            setTripUser(upd);
            return;
        }
        const upd = { ...tripUser, user: { ...tripUser.user, [ev.target.name]: ev.target.value }, _changed: true };
        setTripUser(upd);
    };

    const onCheckboxChange = (ev) => {
        const upd = { ...tripUser, [ev.target.name]: ev.target.checked, _changed: true };
        setTripUser(upd);
    };

    const onSave = (ev) => {
        if (checkIfAllRequiredTags()) {
            setMissingRequiredTagsError(true);
        } else {
            saveChanges(props.idx);
        }
    };

    const saveChanges = async () => {
        setIsSaving(true); // Block Prompt during save
        setLoading(true);
        const ACTION = UPDATE;
        // `precheckin` is the read-only answers summary: never sent back.
        const { precheckin: _precheckin, _ticketChanges, ...tripUserData } = tripUser;
        // CP-5799: staged tickets go to their own routes first. What they saved stays saved even if the
        // participant save below is refused, so the staged list is cleared either way.
        if (hasTicketChanges(_ticketChanges)) {
            const { tickets, errors } = await applyTicketChanges(dataAPI, tripUser.id, _ticketChanges, tripUser.tickets || []);
            errors.forEach((message) => enqueueSnackbar(message, { variant: 'error', autoHideDuration: 8000 }));
            setTripUser((prev) => ({ ...prev, tickets, _ticketChanges: undefined }));
            tripUserData.tickets = tickets;
        }
        const params = {
            id: tripUser.id,
            data: {
                ...tripUserData,
                date: tripUser && tripUser.date ? format(parseISO(tripUser.date), 'yyyy-MM-dd') : tripUser.date,
                tour_id: props.trip.id,
            },
            upload: true,
        };

        if (params.data.attachments.length > 0) {
            for (let index = 0; index < params.data.attachments.length; index++) {
                params.data.attachments[index] = params.data.attachments[index].file;
            }
        }
        if (params.data.voucher) {
            params.data.voucher = params.data.voucher.file;
        }
        await dataAPI(ACTION, resource, params)
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('User saved', { variant: 'success' });
                    props.setLastUpdate(new Date().toString());
                    let res_data = formatUserData(res.data);
                    if (!res_data.role) {
                        res_data = {
                            ...res_data,
                            role: { id: GuestRoleUniqId, name: 'Guest' },
                        };
                    }
                    // Remove _changed flag after successful save to prevent blocking navigation
                    delete res_data._changed;
                    setTripUser(res_data);
                    if (res_data.end_ts) {
                        props.onOwnCheckoutSeen && props.onOwnCheckoutSeen();
                    }
                } else if (res.statusCode === 406) {
                    if (res.data && res.data.message) {
                        enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                    }
                    if (res.data && res.data.errors && res.data.errors.constructor === Array) {
                        res.data.errors.forEach((err) => enqueueSnackbar(err.message || err, { variant: 'error' }));
                    }
                }
            })
            .catch((err, ...args) => console.log('update error', err, args));
        setLoading(false);
        setIsSaving(false); // Allow Prompt again after save is complete
    };

    // The Check-out picker only shows when the booking owns its end (a hotel stay). A
    // booking that derives its end from the tour length shows the toggle below unchecked
    // and no picker -- a read-only datetime next to an editable one reads as "broken",
    // not as "derived". onOwnEndToggle is what flips between the two.
    // CheckoutPicker hands back the instant picked on the tour's clock.
    const onEndTsChange = (endTs) => {
        setTripUser({
            ...tripUser,
            end_ts: endTs,
            _changed: true,
        });
    };

    // Opt a booking into (or out of) its own check-out time. On: reveal the picker,
    // prefilled with the current effective end (trip_end_ts) so it is never empty. Off:
    // clear end_ts, which the edit API reads as "derive the end from the tour again". We
    // set end_ts_type locally only to drive rendering -- the server derives the real
    // value from end_ts presence, so sending a correct end_ts is what matters.
    const onOwnEndToggle = (ev) => {
        if (ev.target.checked) {
            setTripUser({
                ...tripUser,
                end_ts_type: BOOKING_OWN_END,
                end_ts: tripUser.end_ts || tripUser.trip_end_ts || null,
                _changed: true,
            });
        } else {
            setTripUser({
                ...tripUser,
                end_ts_type: END_TS_TYPE_ITINERARY,
                end_ts: null,
                _changed: true,
            });
        }
    };

    // A booking's own check-out moves with its date (CP-8392); the server leaves
    // it where it was unless we send a new one.
    const withTripDate = (date) => ({
        ...tripUser,
        date,
        ...(tripUser.end_ts_type === BOOKING_OWN_END
            ? { end_ts: checkoutForNewTripDate(tripUser.end_ts, tripUser.date, date, tripTimeZone) }
            : {}),
    });

    const onDateChange = (newValue) => {
        const oldValue = tripUser.date ? moment.utc(tripUser.date).format('yyyy-MM-DD') : null;
        if (!window.confirm(`Please confirm a manual date change of the tour from ${tripUser.date} to ${moment.utc(newValue).format('yyyy-MM-DD')}`)) {
            resetToOldValue(oldValue);
        } else {
            if (newValue) {
                checkExistingDate(tripUser.user.email, moment.utc(newValue).format('yyyy-MM-DD'), tripUser.id).then((res) => {
                    if (!res[1]) {
                        if (res[0]) {
                            setSameDateExistsError(true);
                        } else {
                            setTripUser({ ...withTripDate(moment.utc(newValue).format('yyyy-MM-DD')), _changed: true });
                            setSameDateExistsError(false);
                        }
                    } else {
                        setTripUser(withTripDate(newValue ? moment.utc(newValue).format('yyyy-MM-DD') : null));
                        setSameDateExistsError(false);
                    }
                });
            }
        }
    };

    const resetToOldValue = (oldValue) => {
        // Workaround for DatePicker bug. DatePicker won't update the displayed value if it is reset
        // to the original value but will display the new selected value even when cancelled
        setTimeout(() => {
            setTripUser({ ...tripUser, date: moment.utc('1801-01-01').format('yyyy-MM-DD')}); 
            setTripUser({ ...tripUser, date: moment.utc(oldValue).format('yyyy-MM-DD')});
        }, 0);
    };

    const checkExistingDate = async (email_address, date, id, departure_time) => {
        let error = false;
        let same_tour_user = false;
        let params = {
            filter: {
                query: email_address,
                filters: `[{"field":"date","value":"${date}"}]`,
            },
        };
        if (!props.trip.is_bespoke) {
            setLoading(true);
            await dataAPI(GET_LIST, `tour_user/?tour_id=${props.trip.id}`, params)
                .then((res) => {
                    let current_trip_user_departure = tripUser?.departure?.name
                    let trip_has_departures = mergedTagCategories.filter(cat => cat.category.code === 'departure').length > 0
                    if (res.data && res.data.length > 0) {
                        let existing_trip_user = res.data[0]
                        if (existing_trip_user.id === id) {
                            same_tour_user = true;
                            return [error, same_tour_user];

                        }
                        if (existing_trip_user.date === date && existing_trip_user.user.email === email_address) {
                            if (trip_has_departures) {
                                if (departure_time && existing_trip_user.departure.name === departure_time) {
                                    error = true
                                    return [error, same_tour_user];
                                }
                                if (existing_trip_user.departure.name === current_trip_user_departure) {
                                    error = true
                                    return [error, same_tour_user];

                                }
                            }
                            if (!trip_has_departures) {
                                error = true
                                return [error, same_tour_user];

                            }

                        }

                    }

                })
                .finally(setLoading(false));
        }
        return [error, same_tour_user];
    };

    const onRoleChange = (roleId) => {
        let roleMapWithGuest = {
            ...roleMap,
            GuestRoleUniqId: { id: GuestRoleUniqId, name: 'Guest' },
        };
        setTripUser((prev) => ({
            ...prev,
            role: roleMapWithGuest[+roleId.target.value],
            _changed: true,
        }));
    };

    const addNewEmptyTag = () => {
        setTripUser((prev) => {
            let newUserTourTags = [...prev.tags, { idx: prev.tags.length + 1 }];
            // generate new idx for each tag
            for (let i = 0; i < newUserTourTags.length; i++) {
                newUserTourTags[i].idx = i;
            }
            return { ...prev, tags: [...newUserTourTags] };
        });
    };

    const removeTagFromUser = (ev, idx) => {
        setTripUser((prev) => {
            let newUserTourTags = [...prev.tags.filter((tag) => tag.idx !== idx)];
            // generate new idx for each tag
            for (let i = 0; i < newUserTourTags.length; i++) {
                newUserTourTags[i].idx = i;
            }
            return { ...prev, tags: [...newUserTourTags], _changed: true };
        });
    };

    const handleTagCategoryChange = (ev, cat, idx) => {
        ev.stopPropagation();
        setTripUser((prev) => {
            let existing_tags = [...prev.tags.filter((tag) => tag.idx !== idx)];
            existing_tags = [...existing_tags, { idx: idx, category: cat.category }].sort(compare);
            return { ...prev, tags: [...existing_tags] };
        });
    };

    const handleTagChange = (ev, newTag, idx) => {
        let newState = [];
        ev.stopPropagation();

        if (!newTag) {
            setTripUser((prev) => {
                let selected_tag = [...prev.tags.filter((tag) => tag.idx === idx)][0];

                let existing_tags = [...prev.tags.filter((tag) => tag.idx !== idx)].sort(compare);
                return { ...prev, tags: [...existing_tags, { category: { ...selected_tag.category }, idx: idx }] };
            });
            if (!checkIfAllRequiredTags(newState)) {
                setMissingRequiredTagsError(false);
            }
            return;
        }
        setTripUser((prev) => {
            let existing_tags = [...prev.tags.filter((tag) => tag.idx !== idx)].sort(compare);
            return { ...prev, tags: [...existing_tags, { ...newTag, idx: idx }], _changed: true };
        });
        if (!checkIfAllRequiredTags(newState)) {
            setMissingRequiredTagsError(false);
        }
    };

    const onDepartureChange = async (ev, newTag) => {
        if (!newTag) {
            return;
        }
        setTripUser((prev) => {
            const existing_tags = prev.tags.filter((t) => t.category?.code !== 'departure');
            const idx = prev.tags.filter((t) => t.category?.code === 'departure')[0]?.idx;
            return { ...prev, departure: newTag, tags: [...existing_tags, { ...newTag, idx: idx }].sort(compare), _changed: true };
        });
    };

    const checkIfRequiredAndMandatory = (tag) => {
        let result = props.trip.has_departures && tag.category?.code === 'departure';

        if (!result) {
            // get the associated category from the merged categories (trip + operator defaults) for the properties(min/max_tags, chat, location etc..)
            let required_associated_tag_category = mergedTagCategories.filter((cat) => cat.category.id === tag.category?.id)[0];

            // check if required and if there is at least one more already same category required existing tag and that is first or not(sorted by idx)
            if (required_associated_tag_category?.min_tags > 0 && tripUser.tags.filter((utt) => utt?.category?.id === required_associated_tag_category?.category?.id && utt.idx < tag.idx).length === 0) {
                result = true;
            }
        }
        return result;
    };

    const checkIfAllRequiredTags = (newState) => {
        let checkIfNotAllRequiredTagsChanged = [];
        if (newState) {
            checkIfNotAllRequiredTagsChanged = newState.filter((tag) => !tag.id || tag.id === null);
        } else {
            checkIfNotAllRequiredTagsChanged = tripUser.tags.filter((tag) => !tag.id || tag.id === null);
        }
        return checkIfNotAllRequiredTagsChanged.length > 0;
    };

    const onPickupDateTimeChange = (pickupDateTime) => {
        const convertedTime = pickupDateTime ? moment.tz(pickupDateTime, tripTimeZone).format() : null;
        setTripUser((prevState) => ({
            ...prevState,
            tag_context: {
                ...prevState.tag_context,
                pickupDateTime: convertedTime,
            },
            _changed: true,
        }));
    };

    const onQRCodeValueChange = (ev) => {
        setTripUser((prevState) => ({
            ...prevState,
            tag_context: {
                ...prevState.tag_context,
                qrcode: ev.target.value,
            },
            _changed: true,
        }));
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
        };
    };

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    };

    // phone input server validation
    const [phoneServerCheck, setPhoneServerCheck] = React.useState(false);
    const [phoneServerCheckError, setPhoneServerCheckError] = React.useState(false);
    React.useEffect(() => {
        const tm = setTimeout(async () => {
            const params = {
                data: { phone: tripUser.user.phone },
            };
            const testIfAreaCode = (area_code) => {
                let res = /^(\+?\d{1,3}|\d{1,4})$/;
                if (area_code === '+') {
                    setPhoneServerCheckError(false);
                    return true;
                }
                if (res.test(area_code)) {
                    setPhoneServerCheckError(false);
                }
                return res.test(area_code);
            };
            if (tripUser.user.phone && !testIfAreaCode(tripUser.user.phone) && tripUser.user.phone !== '') {
                setPhoneServerCheck(true);
                await dataAPI(CREATE, 'user/validate_phone', params)
                    .then((res) => {
                        if (res.data && !res.data.status) {
                            setPhoneServerCheckError(true);
                        } else {
                            setPhoneServerCheckError(false);
                        }
                    })
                    .finally(() => {
                        setPhoneServerCheck(false);
                    });
            }
        }, 1000);
        return () => {
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripUser.user.phone]);

    const dateTimePickerOnKeyDown = (e) => {
        var key = e.keyCode || e.charCode;
        if (key !== 8) {
            e.preventDefault();
        }
    };

    // attachments handling
    // CP-5559: the idx of the attachment a Replace icon opened the picker for; null when Add attachment did.
    const replaceAttachmentIdx = React.useRef(null);
    const onDrop = React.useCallback(async (acceptedFiles) => {
        let file = acceptedFiles[0];
        if (!file) return;
        const replaceIdx = replaceAttachmentIdx.current;
        let hash_string = await fileToSha1String(file);
        setTripUser((prev) => {
            const others = prev.attachments.filter((existing_file) => existing_file.idx !== replaceIdx);
            if (others.filter((existing_file) => (existing_file.file?.content_hash === hash_string) || (existing_file.content_hash === hash_string)).length > 0) {
                enqueueSnackbar('Duplicate file found, please check existing attachments', { variant: 'default', autoHideDuration: 3000 });
                return { ...prev };
            } else if (replaceIdx !== null) {
                // Saved attachments missing from the list are deleted on SAVE and the new file is uploaded.
                return { ...prev, attachments: prev.attachments.map((existing_file) => (existing_file.idx === replaceIdx ? { file: file, idx: replaceIdx, content_hash: hash_string } : existing_file)), _changed: true };
            } else {
                // Not attachments.length: after a removal that repeats a live idx, and Remove/Replace act by idx.
                const idx = prev.attachments.reduce((max, existing_file) => Math.max(max, existing_file.idx), -1) + 1;
                return { ...prev, attachments: [...prev.attachments, { file: file, idx: idx, content_hash: hash_string }], _changed: true };
            }
        });
    }, []);

    const onVoucherDrop = React.useCallback(async (acceptedFiles) => {
        let file = acceptedFiles[0];
        let hash_string = await fileToSha1String(file);
        setTripUser((prev) => {
            return { ...prev, voucher: { file: file, name: file.name, content_hash: hash_string }, _changed: true };
        })
    }, []);

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const voucherDz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onVoucherDrop,
        multiple: false,
    });

    const handleFileDelete = (idx) => {
        setTripUser((prev) => {
            let file_attachments = prev.attachments.filter((file) => file.idx !== idx);
            return { ...prev, attachments: file_attachments, _changed: true };
        });
    };

    const handleVoucherDelete = (idx) => {
        setTripUser((prev) => {
            return { ...prev, voucher: null, _changed: true };
        });
    };

    const handleOnReplace = (dz, attachmentIdx = null) => {
        replaceAttachmentIdx.current = attachmentIdx;
        dz.open();
    };

    const onPhotoSharingLinkChange = (ev) => {
        setTripUser((prev) => ({ ...prev, _changed: true, tag_context: { ...prev.tag_context, photo_sharing: ev.target.value } }));
    };

    const onTextChange = (ev) => {
        setTripUser((prev) => ({...prev, [ev.target.name]: ev.target.value, _changed: true}));
    }

    const getCorrectCategoryMenuItem = (tag) => {
        if (checkIfRequiredAndMandatory(tag)) {
            return mergedTagCategories
                .filter((tc) => tc.category.code !== 'departure')
                .map((cat) => {
                    return (
                        <MenuItem value={cat.category.id} onClick={(ev) => handleTagCategoryChange(ev, cat, tag.idx)}>
                            {cat.category.name}
                        </MenuItem>
                    );
                });
        } else {
            return allowedTagCategories
                .slice()
                .sort((a, b) => a.category.name.localeCompare(b.category.name))
                .map((cat) => (
                    <MenuItem value={cat.category.id} onClick={(ev) => handleTagCategoryChange(ev, cat, tag.idx)}>
                        {cat.category.name}
                    </MenuItem>
                ));
        }
    };


    const formatTimeValue = (value) => {
        if (!value) return value
        let time = value
        // after the time is selected, it is already in AM/PM format
        if (time.includes("AM") || time.includes("PM")) {
            return time
        }
        if (isUSLocale()) {
            time = convertToAmPm(time)
        }
        return time;
    }
    const onBookingStateChange = (ev) => {
        setTripUser((prev) => ({ ...prev, booking_state: ev.target.value, _changed: true }));
    }

    const onSurveyStateChange = (ev) => {
        setTripUser((prev) => ({ ...prev, survey_state: ev.target.value, _changed: true }));
    }

    const disableSaveButton = tripUser.tags.filter((tag) => !tag.id).length > 0 || !validateEmail(tripUser.user.email) || !canBeSaved || props.syncActive || phoneServerCheck || phoneServerCheckError || sameDateExistsError;
    return (
        <div className={classes.root}>
            {/* <CacheRefreshHistory resource="tour" id={tripUser?.itinerary} open={showCacheHistory} onClose={toggleCacheRefreshHistory} /> */}
            <PageMeta title={`${props.trip.name} - ${props.data.display_name}`} />
            <FixedLinearProgress visibility={loading} />
            <Prompt 
                when={!!tripUser._changed && !isSaving} 
                message="Are you sure you want to leave without saving changes?" 
            />
            <Box m={2}>
                <Grid container direction="row" className={classes.heading}>
                    <Grid item xs={10}>
                        <Typography variant="h6">Edit User Registration</Typography>
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end" alignItems="center">
                        <Tooltip title="Close">
                            <Button aria-label="close-preview" onClick={props.onClose}>
                                <CloseIcon />
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    <Tab label="General" {...a11yProps(0)} />
                    {hasPerm('tour_participant', 'view_history') && <Tab label="Change history" {...a11yProps(1)} />}
                </Tabs>
                <br />
                <TabPanel {...tabPanelProps(0)}>
                    <Grid container direction="column" wrap="nowrap" spacing={2} className={classes.formWrapper}>
                        <Typography style={{ textTransform: 'uppercase', marginLeft: '0.75rem', marginTop: theme.spacing(3), fontSize: '90%' }}>General user information</Typography>
                        {/* {tripUser.order_ref ? (
                            <>
                                <Grid item xs={12}>
                                    <Typography>
                                        Order # {tripUser.order_ref}{' '}
                                        <IconButton size="small" onClick={() => copyToClipboard(tripUser.order_ref, 'Order #')}>
                                            <FileCopyOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Typography>
                                </Grid>
                            </>
                        ) : null} */}
                        <Grid item xs={12}>
                            <TextField
                                name="order_ref"
                                label="Order #"
                                variant="filled"
                                size="small"
                                fullWidth
                                value={tripUser.order_ref || ''}
                                onChange={onTextChange}
                            />
                        </Grid>
                        {tripUser.affiliate_ext_id ? (
                            <>
                                <Grid item xs={12}>
                                    <Typography>
                                        Affiliate booking # {tripUser.affiliate_ext_id}{' '}
                                        <Tooltip title="Copy affiliate booking #">
                                            <IconButton size="small" onClick={() => copyToClipboard(tripUser.affiliate_ext_id, 'Affiliate booking #')}>
                                                <FileCopyOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                </Grid>
                            </>
                        ) : null}
                        <Grid item>
                            <RemoteAutocomplete
                                apiFilters={props.apiFilters}
                                disabled={!hasWritePerm}
                                getOptionLabel={(option) => option.email}
                                resource="user"
                                urlSuffix={null}
                                label="Email *"
                                variant="filled"
                                size="small"
                                onChange={onExistingUserSelect}
                                onQueryChange={onUserEmailChange}
                                value={tripUser.user}
                                textFieldProps={{ ...errors.email }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField {...errors.first_name} disabled={!hasWritePerm} variant="filled" size="small" fullWidth name="first_name" label="First Name" value={tripUser.user.first_name || ''} onChange={onChange} />
                        </Grid>
                        <Grid item>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" fullWidth name="last_name" label="Last Name" value={tripUser.user.last_name || ''} onChange={onChange} />
                        </Grid>
                        <Grid item>
                            <MaterialUiPhoneNumber
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                name="phone"
                                label={tripUser.user.phone && tripUser.user.sms_is_undeliverable ? <Tooltip title="Number is unreachable"><div style={{ display: "flex", alignItems: "center" }}>Phone&nbsp;<ErrorOutlineIcon style={{ color: "#999999", height: "18px", width: "18px" }} /></div></Tooltip> : "Phone"}
                                fullWidth
                                defaultCountry="us"
                                inputProps={{
                                    name: 'phone',
                                }}
                                onChange={handleOnPhoneChange}
                                value={tripUser.user.phone ?? ''}
                                InputProps={{
                                    endAdornment: phoneServerCheck ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} color="primary" />
                                        </InputAdornment>
                                    ) : null,
                                }}
                                helperText={phoneServerCheckError && 'Please enter a valid phone number'}
                                error={phoneServerCheckError}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="filled" size="small" disabled={!hasWritePerm}>
                                <InputLabel id="bookingState">Booking state</InputLabel>
                                <Select name="booking_state" labelId="bookingState" id="bookingState" value={tripUser.booking_state} onChange={onBookingStateChange} >
                                    {bookingStatesMenuItems}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="filled" size="small" disabled={!hasWritePerm}>
                                <InputLabel id="surveyState">Post-trip survey</InputLabel>
                                <Select name="survey_state" labelId="surveyState" id="surveyState" value={tripUser.survey_state} onChange={onSurveyStateChange} >
                                    {surveyStatesMenuItems}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <RemoteAutocomplete disabled={!hasWritePerm} getOptionLabel={(opt) => opt.name ?? opt} resource="user" urlSuffix={'language_list'} label="Language" variant="filled" size="small" fullWidth onChange={onLanguageChange} value={tripUser.user.lang} />
                        </Grid>
                        <Typography style={{ textTransform: 'uppercase', marginLeft: '0.75rem', fontSize: '90%', marginTop: '2rem' }}>tour specific information</Typography>

                        {!props.trip.is_bespoke && (
                            <Grid item xs={12} container>
                                <DatePicker
                                    value={tripUser.date ? moment.utc(tripUser.date) : null}
                                    onChange={onDateChange}
                                    disabled={props.syncActive || (props.trip.length > 0 && moment.utc(tripUser.date).add('days', Math.ceil(props.trip.length / 86400)) < moment.utc()) || !hasWritePerm}
                                    format={'LL'}
                                    fullWidth
                                    label="Trip Date"
                                    required
                                    minDate={nonBespokeStartDate ? moment.utc(nonBespokeStartDate) : null}
                                    maxDate={nonBespokeEndDate ? moment.utc(nonBespokeEndDate) : null}
                                    error={sameDateExistsError}
                                    slotProps={{
                                        textField: {
                                            variant: 'filled',
                                        },
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Tooltip title="Give this booking its own check-out time instead of deriving it from the tour length">
                                <span>
                                    <FormControlLabel
                                        disabled={props.syncActive || !hasWritePerm}
                                        control={<Checkbox
                                            color="primary"
                                            name="own_end"
                                            checked={tripUser.end_ts_type === BOOKING_OWN_END}
                                            onChange={onOwnEndToggle} />}
                                        label="This booking has its own check-out time" />
                                </span>
                            </Tooltip>
                        </Grid>
                        {tripUser.end_ts_type === BOOKING_OWN_END && (
                            <Grid item xs={12} container>
                                <CheckoutPicker
                                    timeZone={tripTimeZone}
                                    tripDate={tripUser.date}
                                    value={tripUser.end_ts}
                                    onChange={onEndTsChange}
                                    disabled={props.syncActive || !hasWritePerm}
                                />
                            </Grid>
                        )}
                        {!props.trip.is_bespoke && props.trip.has_departures ? (
                            <Grid item xs={12}>
                                <RemoteAutocomplete
                                    disabled={!hasWritePerm}
                                    apiFilters={[
                                        { field: 'category_code', op: 'eq', value: 'departure' },
                                        { field: 'tour_id', op: 'eq' },
                                    ]}
                                    filterOptions={(options) => options}
                                    value={{ ...tripUser.departure, name: formatTimeValue(tripUser.departure?.name) }}
                                    // sort by time and format to AM/PM if needed
                                    mapResults={(list, res) => list.sort((a, b) => {
                                        return a?.tag?.name?.localeCompare(b.tag.name);
                                    }).map((x) => ({ ...x.tag, name: formatTimeValue(x.tag.name) }))}
                                    resource="tour"
                                    urlSuffix={`${props.trip.id}/departures/`}
                                    label="Select departure"
                                    variant="filled"
                                    size="small"
                                    required
                                    onChange={onDepartureChange}
                                />
                            </Grid>
                        ) : null}

                        {/* The operator's code for this departure INSTANCE. Sits next to
                            the picker above, which selects a time-of-day tag ("18:00") --
                            two departures of one tour on the same date and time differ
                            only by this code. Editing it MOVES the booking between
                            departures and locks the value against the next sync. */}
                        <Grid item xs={12}>
                            <TextField
                                name="departure_key"
                                label="Departure code"
                                variant="filled"
                                size="small"
                                fullWidth
                                disabled={!hasWritePerm}
                                value={tripUser.departure_key || ''}
                                onChange={onTextChange}
                                helperText={tripUser.departure_key_locked ? 'Set manually — imports will not overwrite it' : ''}
                            />
                        </Grid>

                        {sameDateExistsError && (
                            <Grid item xs={12}>
                                <Typography color="secondary" style={{ fontSize: '0.85rem', marginLeft: '1rem', marginTop: '-0.75rem' }}>
                                    User is already registered on this date
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={!hasWritePerm}>
                                <InputLabel id="user-role" style={{ paddingTop: '1rem' }}>
                                    Role
                                </InputLabel>
                                <Select disabled={!hasWritePerm || allowed_roles.length === 1} labelId="user-role" value={tripUser.role?.id} onChange={onRoleChange} variant="filled" size="small" style={{ textTransform: 'capitalize' }}>
                                    {allowed_roles.map((r) => {
                                        return (
                                            <MenuItem key={r?.id} value={r?.id}>
                                                {toTitleCase(r?.name)}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <TextField {...errors.booking_notes} disabled={!hasWritePerm} variant="filled" size="small" minRows={3} multiline fullWidth name="booking_notes" label="Booking Notes" value={tripUser.booking_notes || ''} onChange={onChange} />
                        </Grid>
                        <Grid item>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" minRows={3} multiline fullWidth name="checkin_notes" label="Check-in Notes" value={tripUser.checkin_notes || ''} onChange={onChange} />
                        </Grid>
                        {tripUser.tags
                            .filter((x) => x.category?.code !== 'departure')
                            .sort(compare)
                            ?.map((tag) => {
                                return (
                                    <Grid item xs={12} container alignItems="center" key={tag}>
                                        <Grid item xs={4} style={{ paddingRight: '0.1rem' }}>
                                            {tag.category?.id ? (
                                                <TextField label={checkIfRequiredAndMandatory(tag) ? 'Category *' : 'Category'} variant="filled" size="small" disabled={true} value={tag.category.name} />
                                            ) : (
                                                    <FormControl fullWidth variant="filled" size="small" disabled={!hasWritePerm}>
                                                    <InputLabel id="category-select">{checkIfRequiredAndMandatory(tag) ? 'Category *' : 'Category'}</InputLabel>

                                                    <Select
                                                        labelId="category-select"
                                                        value={tag.category?.id ? tag.category.id : ''}
                                                        disabled={checkIfRequiredAndMandatory(tag)}
                                                        MenuProps={{
                                                            style: {
                                                                zIndex: 10000,
                                                            },
                                                        }}
                                                    >
                                                        {getCorrectCategoryMenuItem(tag)}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        {tag.category && (
                                            <Grid item xs={!checkIfRequiredAndMandatory(tag) ? 6 : 7}>
                                                <RemoteAutocomplete
                                                    disabled={!hasWritePerm}
                                                    apiFilters={[{ field: 'category', op: 'eq', value: [tag.category?.id?.toString()] }]}
                                                    value={tag}
                                                    resource="tour_tag"
                                                    urlSuffix={null}
                                                    label="Select Tag *"
                                                    variant="filled"
                                                    size="small"
                                                    onChange={(ev, nt) => handleTagChange(ev, nt, tag.idx)}
                                                />
                                            </Grid>
                                        )}
                                        {!checkIfRequiredAndMandatory(tag) ? (
                                            <Grid item xs={1} container justifyContent="flex-end">
                                                <DeleteIconButton
                                                    title="Remove tag"
                                                    fontSize="medium"
                                                    sx={{ p: 0 }}
                                                    allowed={hasWritePerm}
                                                    tooltipProps={ABOVE_PANEL_TOOLTIP}
                                                    onClick={(ev) => removeTagFromUser(ev, tag.idx)}
                                                />
                                            </Grid>
                                        ) : null}
                                        {mergedTagCategories.filter((tc) => tc.category?.id === tag.category?.id && tc.chat_conversation).length > 0 && (
                                            <Grid item xs={1} container justifyContent="flex-end">
                                                <Tooltip title={'This tag is part of chat conversation'}>
                                                    <ChatIcon />
                                                </Tooltip>
                                            </Grid>
                                        )}
                                    </Grid>
                                );
                            })}
                        {missingRequiredTagsError && (
                            <Grid xs={12}>
                                <Typography color="secondary" style={{ margin: '1rem' }}>
                                    Please add all required tags.
                                </Typography>
                            </Grid>
                        )}

                        {allowedTagCategories.length > 0 && hasWritePerm && (
                            <Grid item xs={12}>
                                <Button color="primary" variant="outlined" onClick={addNewEmptyTag}>
                                    Add tag
                                </Button>
                            </Grid>
                        )}

                        <PrecheckinAnswers precheckin={tripUser.precheckin} />

                        {props.trip.is_bespoke ? null : (
                            <>
                                <Typography style={{ textTransform: 'uppercase', marginLeft: '0.75rem', fontSize: '90%', marginTop: '2rem' }}>User specific information</Typography>
                                <Grid item xs={12}>
                                    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                                        <TimePicker
                                            disabled={!hasWritePerm}
                                            label="Pickup time"
                                            value={tripUser.tag_context?.pickupDateTime ? moment.utc(tripUser.tag_context.pickupDateTime).tz(tripTimeZone) : null}
                                            onChange={onPickupDateTimeChange}
                                            slotProps={{
                                                textField: {
                                                    variant: 'filled',
                                                    size: 'small',
                                                    helperText: null,
                                                    onKeyDown: dateTimePickerOnKeyDown,
                                                    error: false,
                                                },
                                                actionBar: {
                                                    actions: ['clear', 'accept'],
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField disabled={!hasWritePerm} label="QR Code Value" variant="filled" value={tripUser.tag_context?.qrcode ?? ''} onChange={onQRCodeValueChange} error={false} helperText={null} fullWidth />
                                </Grid>
                                {!tripUser.public_qr_image ? null : (
                                    <Grid item xs={12}>
                                        <Typography>Booking QR code</Typography>
                                        <img
                                            alt="QR"
                                            src={tripUser.public_qr_image}
                                            style={{
                                                width: '128px',
                                                height: '128px',
                                            }}
                                        />
                                        ;
                                    </Grid>
                                )}
                            </>
                        )}
                        {tripUser.guide_sr_link && (
                            <>
                                <Typography style={{ textTransform: 'uppercase', marginLeft: '0.75rem', fontSize: '90%', marginTop: '2rem' }}>Guide self-registration link</Typography>
                                <Grid item xs={12}>
                                    <Link href={tripUser.guide_sr_link} target="_blank">
                                        <QRCodeCanvas
                                            value={tripUser.guide_sr_link}
                                            includeMargin={true}
                                            style={{
                                                width: '128px',
                                                height: '128px',
                                            }}
                                        />
                                    </Link>
                                </Grid>
                            </>
                        )}
                        
                        <Grid item xs={12}>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" fullWidth name="photo_sharing" label="Photo Sharing URL (optional)" value={tripUser.tag_context?.photo_sharing || ''} onChange={onPhotoSharingLinkChange} />
                        </Grid>
                        <Grid item xs={12} spacing={1} container direction="row">
                            <Grid item xs={12} mt={2}>
                                <Typography fontSize={16} fontWeight={500}>
                                    Airbnb (if applicable)
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    name="airbnb_direct_user_thread_id"
                                    label="Airbnb Direct User Thread ID"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    value={tripUser.airbnb_direct_user_thread_id || ''}
                                    onChange={onTextChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    name="airbnb_channel_id"
                                    label="Airbnb Thread ID"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    value={tripUser.airbnb_channel_id || ''}
                                    onChange={onTextChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    name="airbnb_host_id"
                                    label="Airbnb Host Account ID"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    value={tripUser.airbnb_host_id || ''}
                                    onChange={onTextChange}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12} mt={2}>
                            <Typography fontSize={16} fontWeight={500}>
                                Voucher
                            </Typography>
                        </Grid>
                        <input {...voucherDz.getInputProps()} />
                        { tripUser.voucher && (
                        <Grid item xs={12}>
                            {/* CP-5559: the shared file row (name opens the file, Replace, trash) instead of [MODIFY VOUCHER]. */}
                            <FileActionRow
                                name={tripUser.voucher.name}
                                file={tripUser.voucher.file}
                                onReplace={() => handleOnReplace(voucherDz)}
                                onDelete={() => handleVoucherDelete()}
                                replaceTitle="Replace voucher"
                                deleteTitle="Remove voucher"
                                disabled={!hasWritePerm}
                                tooltipProps={ABOVE_PANEL_TOOLTIP}
                            />
                        </Grid> )}
                        {hasWritePerm && !tripUser.voucher && <Grid item xs={12} color="primary">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    handleOnReplace(voucherDz);
                                }}
                            >
                                <AddIcon fontSize="small"/>&nbsp;Add voucher
                            </Button>
                        </Grid>}

                        <Grid item xs={12} mt={2}>
                            <Typography fontSize={16} fontWeight={500}>
                                Attachments
                            </Typography>
                        </Grid>
                        {tripUser.attachments.map((file) => (
                            <Grid item xs={12} key={file.idx} style={{ backgroundColor: '#F5F5F5', height: '48px', borderRadius: '8px', display: 'flex', marginLeft: '0.75rem', minWidth: 0 }} mt={0.5} p={2} container justifyContent="space-between" alignItems="center" alignContent="center">
                                {loading ? (
                                    <Typography color="#c8c8c8">Loading...</Typography>
                                ) : (
                                    // CP-5559: Replace (blue) beside the trash.
                                    <FileActionRow
                                        name={file.file?.name}
                                        file={file.file}
                                        onReplace={() => handleOnReplace(dz, file.idx)}
                                        onDelete={() => handleFileDelete(file.idx)}
                                        replaceTitle="Replace attachment"
                                        deleteTitle="Remove attachment"
                                        disabled={!hasWritePerm}
                                        actionsAtEnd
                                        tooltipProps={ABOVE_PANEL_TOOLTIP}
                                        sx={{ width: '100%' }}
                                    />
                                )}
                            </Grid>
                        ))}
                        <input {...dz.getInputProps()} />
                        {hasWritePerm && <Grid item xs={12} color="primary">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    handleOnReplace(dz);
                                }}
                            >
                                <AddIcon fontSize="small"/>&nbsp;Add attachment
                            </Button>
                        </Grid>}

                        <BookingTickets
                            tickets={tripUser.tickets}
                            changes={tripUser._ticketChanges}
                            onChangesChange={(next) => setTripUser((prev) => ({ ...prev, _ticketChanges: next, _changed: true }))}
                            integration={tripUser.integration}
                            canEdit={hasWritePerm}
                            tooltipProps={ABOVE_PANEL_TOOLTIP}
                        />
                        {props.trip.has_group_chat && (
                            <Grid item xs={12} mt={2}>
                                <Tooltip title={"Exclude this user from group chat" + (tripUser.user.exclude_from_group_chat ? ' (user has group chat excluded in their profile)' : '')}>
                                    <span>
                                        <FormControlLabel
                                            disabled={!hasWritePerm || tripUser.user.exclude_from_group_chat}
                                            control={<Checkbox
                                                color="primary"
                                                name="exclude_from_group_chat"
                                                checked={tripUser.exclude_from_group_chat || tripUser.user.exclude_from_group_chat}
                                                onChange={onCheckboxChange} />}
                                            label="Exclude from group chat" />
                                    </span>
                                </Tooltip>
                            </Grid>
                        )}
                        <Grid item xs={12} mt={2}>
                            <Typography style={{ textTransform: 'uppercase', fontSize: '90%', marginTop: '2rem' }}>Booking information</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                Created at {moment(tripUser.created_ts).format('YYYY-MM-DD HH:mm:ss Z')}
                                { tripUser.create_source !== null ? ` (${SOURCES[+tripUser.create_source]})` : null }
                            </Typography>
                            <Typography>
                                Last updated at: {moment(tripUser.updated_ts).format('YYYY-MM-DD HH:mm:ss Z')}
                                { tripUser.update_source !== null ? ` (${SOURCES[+tripUser.update_source]})` : null }
                            </Typography>
                            { tripUser.people_count && (<Typography>
                                People count: {tripUser.people_count}{' '}
                            </Typography>) }
                            { tripUser.reseller_ref && (<Typography>
                                Reseller reference: {tripUser.reseller_ref}{' '}
                                <Tooltip title="Copy reseller reference">
                                    <IconButton size="small" onClick={() => copyToClipboard(tripUser.reseller_ref, 'Reseller #')}>
                                        <FileCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Typography>) }
                            { tripUser.supplier_ref && (<Typography>
                                Supplier reference: {tripUser.supplier_ref}{' '}
                                <Tooltip title="Copy supplier reference">
                                    <IconButton size="small" onClick={() => copyToClipboard(tripUser.supplier_ref, 'supplier #')}>
                                        <FileCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Typography>) }
                            { tripUser.custom_id && (
                                <Typography>
                                    Booking {tripUser.custom_id}{' '}
                                    <Tooltip title="Copy booking ID">
                                        <IconButton size="small" onClick={() => copyToClipboard(tripUser.custom_id, 'Order #')}>
                                            <FileCopyOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                            )}
                        </Grid>
                        {/* <Box m={2} className={classes.nearlyHidden}>
                            <span onClick={toggleCacheRefreshHistory}>Itinerary {tripUser.itinerary}</span>
                        </Box> */}
                    </Grid>
                </TabPanel>
                {currentTab === 1 && <Box className={classes.changeHistoryWrapper}>
                    <RevisionHistoryDialog open={currentTab === 1} resource={resource} id={tripUser.id} style={{ height: props.isSmallScreen ? 'calc(100vh - 280px)' : 'calc(100vh - 465px)' }} />
                </Box>}
                <Grid item xs={12} container className={classes.buttonsPanel}>
                    <Grid item xs={12} pt={props.isSmallScreen ? 0 : 2}>
                        <FormActionButtons
                            mode="edit"
                            size="medium"
                            submitRef={saveRef}
                            onSubmit={onSave}
                            submitDisabled={!hasPerm('tour_participant', 'write') || disableSaveButton}
                            onCancel={props.onClose}
                            cancelDisabled={props.syncActive}
                            leadingActions={tripUser.id && hasPerm('ops', 'change_booking_user') ? (
                                <Button color="primary" variant="outlined" startIcon={<CachedIcon />} onClick={() => setChangeOwnerOpen(true)}>
                                    Change owner…
                                </Button>
                            ) : null}
                        />
                    </Grid>
                </Grid>
                {tripUser.id && hasPerm('ops', 'change_booking_user') && (
                    <ChangeBookingOwnerDialog
                        open={changeOwnerOpen}
                        onClose={() => setChangeOwnerOpen(false)}
                        tripUser={tripUser}
                        onChanged={() => {
                            props.onClose && props.onClose();
                        }}
                    />
                )}
            </Box>
        </div>
    );
};

const TourUserPreview = (props) => {
    const resource = "tour_user";
    const { hasPerm } = React.useContext(ContextStore);

    const handleOnLoad = (data) => {
        if (data?.end_ts) {
            props.onOwnCheckoutSeen && props.onOwnCheckoutSeen();
        }
        props.onLoad && props.onLoad(props.id, data);
    };
    if (hasPerm('tour_participant', 'read') === false) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={props.id} onLoad={handleOnLoad}>
            <RecordDetailsContext.Consumer>
                {(data) => (
                    <TripUserPreviewPanel
                        resource={props.resource}
                        id={props.id}
                        data={formatUserData(data)}
                        onClose={props.onClose}
                        onDelete={props.onDelete}
                        onChange={props.onChange}
                        onUserUpdateSave={props.onSave}
                        trip={props.trip}
                        setLastUpdate={props.setLastUpdate}
                        onOwnCheckoutSeen={props.onOwnCheckoutSeen}
                        isSmallScreen={props.isSmallScreen}
                    />
                )}
            </RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default TourUserPreview;
